import Box from 'UIKit/Box/Box'
import Grid from 'UIKit/Box/Grid'
import EmptyContent from 'UIKit/EmptyContent'
import HunnyLazyList from 'UIKit/HunnyLazyList/HunnyLazyList'
import Modal, { ModalHeader, ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import TransText from 'UIKit/Text/TransText'
import { useFreeSpinCreateGameSession } from 'components/GameListModal/hooks'
import { Token } from 'config/types'
import { Game, GamePlayAction, GameSession } from 'config/types/game'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useIsTelegramOrigin } from 'hooks/useTelegram'
import { RowCenter, RowMiddle } from 'layout/Components/Row'
import React, { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { BaseResponse, HunnyRequest, Paging } from 'services/types'
import { useAppDispatch } from 'state'
import { selectPlayBalance } from 'state/session/actions'
import styled from 'styled-components'
import base from 'theme/base'
import { delayed } from 'utils'
import GameItem, { StyledWrapper } from 'views/Games/components/GameItem'
import GameItemLoader from 'views/Games/components/GameItemLoader'
import SearchInput from './SearchInput'
import { useSearchGames } from './useSearchGame'

interface GameListModalProps {
  titleHeader: string
  contentDescription: string
  request: (offset: number, limit: number) => HunnyRequest<BaseResponse<Paging<any>>>
  onGameSelected?: () => void
  // Note: These props are crucial for game play behavior
  // - playWithSelectedToken: Determines if free spins are available
  // - playAction: Takes priority in defining the game play action
  playWithSelectedToken?: Token
  playAction?: GamePlayAction
}

const buildLazyGameItem =
  (
    playAction: GamePlayAction,
    handlePlay?: (game: Game) => void,
    handleFetchGame?: (game: Game) => Promise<BaseResponse<GameSession>>,
  ) =>
  ({ item }) =>
    (
      <GameItem
        game={item}
        p="0px !important"
        gamePlayAction={playAction}
        handlePlayFn={handlePlay}
        handleFetchGame={handleFetchGame}
      />
    )

const GamesLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Grid
      position="relative"
      gridGap="8px"
      gridTemplateColumns={['1fr 1fr', '1fr 1fr', '1fr 1fr 1fr 1fr', '1fr 1fr 1fr 1fr']}
    >
      {children}
    </Grid>
  )
}

const Loader = () => (
  <>
    <GameItemLoader p="0px !important" />
    <GameItemLoader p="0px !important" />
    <GameItemLoader p="0px !important" />
  </>
)

const EligibleGamesModal: React.FC<
  ModalProps<GameListModalProps & { gameCollectionId: number; totalGames: number }>
> = ({ data, onDismiss }) => {
  const { titleHeader, playWithSelectedToken, request, playAction, onGameSelected, gameCollectionId, totalGames } = data
  const dispatch = useAppDispatch()
  const { fetchFn } = useFreeSpinCreateGameSession(data.playWithSelectedToken)
  const isTelegramOrigin = useIsTelegramOrigin()
  const { isMobile } = useMatchBreakpoints()
  const [isFetchMobileGameSession, setIsFetchMobileGameSession] = useState(false)

  const [searchContent, setSearchContent] = useState('')
  const isDisplayGamesSearch = searchContent.length > 0
  const { gamesSearch, isSearching } = useSearchGames(searchContent, gameCollectionId)

  const updateSearchContent = (value: string) => {
    setSearchContent(value)
  }

  const LazyItem = useMemo(() => {
    return buildLazyGameItem(
      playAction ?? (playWithSelectedToken ? GamePlayAction.MakeSpinWithSelectedCurrency : GamePlayAction.MakeSpin),
      () => {
        if (playWithSelectedToken) {
          dispatch(selectPlayBalance({ token: playWithSelectedToken }))
        }

        if (onGameSelected) {
          onGameSelected()
        }
      },
      async (game) => {
        if (!isMobile) return null

        if (playWithSelectedToken) {
          setIsFetchMobileGameSession(true)
          dispatch(selectPlayBalance({ token: playWithSelectedToken }))
          const gameSessionData = await fetchFn(game)
          await delayed(500)

          if (!isTelegramOrigin) {
            window.location.href = gameSessionData.gameUrl
            onDismiss()
          }

          setIsFetchMobileGameSession(false)

          return gameSessionData
        }
      },
    )
  }, [playWithSelectedToken, playAction])

  return (
    <>
      {isFetchMobileGameSession && (
        <Box
          width="100vw"
          height="100vh"
          position="fixed"
          zIndex={base.zIndices.modal}
          style={{ userSelect: 'none' }}
        />
      )}
      <StyledModal onDismiss={onDismiss}>
        <ModalHeader>
          <RowMiddle width="100%" px={['12px', '', '24px']}>
            <TransText fontSize="16px" fontWeight={700}>
              {titleHeader}
            </TransText>
            <Text fontSize="16px" fontWeight={700} ml="4px">
              ({totalGames})
            </Text>
          </RowMiddle>
        </ModalHeader>

        <Box width="100%" pb={['12px', '', '24px']}>
          <Box px={['12px', '', '24px']}>
            <SearchInput updateSearchContent={updateSearchContent} />
            {!isDisplayGamesSearch && (
              <Text
                color="textAlt1"
                fontSize="12px"
                fontWeight={500}
                letterSpacing={-0.24}
                mb="8px"
                style={{ wordBreak: 'break-word' }}
              >
                <Trans>Please choose a desirable game to join Tourament.</Trans>
              </Text>
            )}
          </Box>

          <Container overflowX="hidden" position="relative">
            <Box
              overflow="auto"
              px={['12px', '', '24px']}
              maxHeight={['calc(var(--screen-height) - 25vh)', '', '', '414px']}
              minHeight={['calc(var(--screen-height) - 25vh)', '', '', '414px']}
            >
              {isSearching ? (
                <GamesLayout>
                  <Loader />
                </GamesLayout>
              ) : (
                <>
                  {isDisplayGamesSearch && gamesSearch.length === 0 ? (
                    <RowCenter minHeight={['calc(var(--screen-height) - 25vh)', '', '', '414px']}>
                      <TransText fontSize="16px" fontWeight={500} color="textAlt5" letterSpacing="-0.32px">
                        No Games match your search
                      </TransText>
                    </RowCenter>
                  ) : (
                    <GamesLayout>
                      {isDisplayGamesSearch ? (
                        <>
                          {gamesSearch.map((game) => (
                            <LazyItem item={game} />
                          ))}
                        </>
                      ) : (
                        <HunnyLazyList
                          uniqueKey="code"
                          Item={LazyItem}
                          noContent={
                            <Box position="absolute" top="64px" width="100%">
                              <EmptyContent justifyContent="center" width="100%" content="We couldn't find any games" />
                            </Box>
                          }
                          Loader={<Loader />}
                          request={request}
                          limit={20}
                        />
                      )}
                    </GamesLayout>
                  )}
                </>
              )}
            </Box>
          </Container>
        </Box>
      </StyledModal>
    </>
  )
}

const StyledModal = styled(Modal)`
  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    width: 610px;
    min-width: 610px;
    min-height: 590px;
    border-radius: ${({ theme }) => theme.radii.large};
  }
  ${StyledWrapper} {
    width: 100%;
    height: unset;
  }

  .wrap-header-container {
    background: none !important;
  }
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;

  width: 100%;
  min-width: 100%;

  background: ${({ theme }) => theme.colors.modalBackground};
  border-radius: ${({ theme }) => theme.radii.modal};

  overflow-y: auto;

  ::-webkit-scrollbar-track {
    margin: 16px 0;
  }
`
export default EligibleGamesModal
